/**
 * The browser's localStorage, or `null` when it's unavailable.
 * Reading `window.localStorage` throws a SecurityError when site data is blocked, and some in-app
 * browsers (e.g. TikTok's Android WebView) disable DOM storage so it reads as `null`.
 * @returns localStorage, or `null` on the server or when storage is unavailable
 */
const getLocalStorage = (): Nullable<Storage> => {
  /* istanbul ignore next */
  if (typeof window === 'undefined') return null

  try {
    // eslint-disable-next-line no-restricted-properties -- the one place allowed to touch it
    return window.localStorage ?? null
  } catch {
    return null
  }
}

/**
 * localStorage that never throws. Reads return `null` and writes report `false` when storage is
 * unavailable, blocked or full, so callers fall back to keeping the value for this page view.
 * Use it instead of `localStorage` directly.
 */
export const safeLocalStorage = {
  getItem: (key: string): Nullable<string> => {
    try {
      return getLocalStorage()?.getItem(key) ?? null
    } catch {
      return null
    }
  },
  /** @returns whether storage now holds the value */
  setItem: (key: string, value: string): boolean => {
    try {
      const storage = getLocalStorage()
      if (!storage) return false
      storage.setItem(key, value)
      return true
    } catch {
      return false
    }
  },
  /** @returns whether storage no longer holds the key */
  removeItem: (key: string): boolean => {
    try {
      const storage = getLocalStorage()
      if (!storage) return false
      storage.removeItem(key)
      return true
    } catch {
      return false
    }
  },
}
